import { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Link, useHistory, useParams, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { withTranslation } from "react-i18next";
import dayjs from "dayjs";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import Loader from "../../Components/Common/Loader";
import StockStatusBadge from "./components/StockStatusBadge";
import { formatPrice } from "../../constants/promotedProducts";
import {
  useDeletePromotedProduct,
  useInvalidatePromotedProduct,
  usePromotedProduct,
  useUpdateProductStock,
  useValidatePromotedProduct,
} from "../../hooks/usePromotedProducts";
import { normalizeBackendFileUrl } from "../../utils/backendApi";
import { resolveApiErrorMessage } from "../../utils/resolveApiErrorMessage";
import defaultIllustration from "../../assets/images/lcbtp.png";

const ProductDetails = (props) => {
  const { id } = useParams();
  const history = useHistory();

  const productQ = usePromotedProduct(id);
  const validateMutation = useValidatePromotedProduct();
  const invalidateMutation = useInvalidatePromotedProduct();
  const stockMutation = useUpdateProductStock();
  const deleteMutation = useDeletePromotedProduct();

  const [activeImage, setActiveImage] = useState(0);
  const [showDelete, setShowDelete] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const [stockValue, setStockValue] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const product = productQ.data;
  const images = product?.images ?? [];

  const handleDelete = () => {
    deleteMutation.mutate(id, {
      onSuccess: () => history.push("/promoted-products"),
      onSettled: () => setShowDelete(false),
    });
  };

  const handleStockUpdate = () => {
    stockMutation.mutate(
      { id, stockQuantity: Number(stockValue) },
      { onSettled: () => setShowStock(false) }
    );
  };

  const handleReject = () => {
    invalidateMutation.mutate(
      { id, rejectionReason: rejectReason },
      { onSettled: () => setShowReject(false) }
    );
  };

  document.title = props.t("Détail du produit");

  if (productQ.isLoading) return <Loader />;
  if (productQ.isError) {
    return <Loader error={resolveApiErrorMessage(productQ.error, "Erreur")} />;
  }

  const cover = images[activeImage] || images.find((i) => i.is_cover) || images[0];

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <DeleteModal
        show={showDelete}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDelete(false)}
      />

      {/* Quick stock adjustment */}
      <Modal isOpen={showStock} toggle={() => setShowStock(false)} centered>
        <ModalHeader toggle={() => setShowStock(false)}>
          Ajuster le stock
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="stockValue">Quantité en stock</Label>
          <Input
            id="stockValue"
            type="number"
            min="0"
            step="1"
            value={stockValue}
            onChange={(e) => setStockValue(e.target.value)}
          />
          <small className="text-muted">
            Le statut est recalculé automatiquement par le serveur.
          </small>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowStock(false)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleStockUpdate}
            disabled={stockMutation.isPending || stockValue === ""}
          >
            {stockMutation.isPending ? "Enregistrement…" : "Enregistrer"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Invalidation with reason */}
      <Modal isOpen={showReject} toggle={() => setShowReject(false)} centered>
        <ModalHeader toggle={() => setShowReject(false)}>
          Retirer ce produit du mobile
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="rejectReasonDetail">Motif</Label>
          <Input
            id="rejectReasonDetail"
            type="textarea"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex. Photos de mauvaise qualité, prix non renseigné."
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowReject(false)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleReject}
            disabled={invalidateMutation.isPending}
          >
            {invalidateMutation.isPending ? "En cours…" : "Invalider"}
          </button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <BreadCrumb
          title={product.designation}
          pageTitle={props.t("Promotion de produits")}
        />

        {/* Validation state banner */}
        <div
          className={`alert ${
            product.is_validated ? "alert-success" : "alert-warning"
          } d-flex align-items-center justify-content-between`}
        >
          <div>
            <i
              className={`me-2 ${
                product.is_validated
                  ? "ri-checkbox-circle-line"
                  : "ri-time-line"
              }`}
            />
            {product.is_validated ? (
              <>
                Ce produit est <b>visible sur l&apos;application mobile</b>
                {product.validated_by?.full_name
                  ? ` — validé par ${product.validated_by.full_name}`
                  : ""}
                {product.validated_at
                  ? ` le ${dayjs(product.validated_at).format("DD/MM/YYYY")}`
                  : ""}
                .
              </>
            ) : (
              <>
                Ce produit <b>n&apos;est pas encore visible</b> sur
                l&apos;application mobile. Cliquez sur <b>Valider</b> pour le
                publier.
              </>
            )}
          </div>

          <div className="d-flex gap-2">
            {product.is_validated ? (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => {
                  setRejectReason(product.rejection_reason || "");
                  setShowReject(true);
                }}
              >
                Invalider
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={() => validateMutation.mutate(id)}
                disabled={validateMutation.isPending || images.length === 0}
                title={
                  images.length === 0
                    ? "Ajoutez au moins une image pour pouvoir valider"
                    : undefined
                }
              >
                {validateMutation.isPending ? "Validation…" : "Valider"}
              </button>
            )}
          </div>
        </div>

        {product.rejection_reason ? (
          <div className="alert alert-secondary">
            <b>Motif du dernier rejet :</b> {product.rejection_reason}
          </div>
        ) : null}

        <Row>
          {/* Gallery */}
          <Col lg={5}>
            <Card>
              <CardBody>
                <img
                  src={
                    normalizeBackendFileUrl(cover?.image) || defaultIllustration
                  }
                  alt={product.designation}
                  className="rounded w-100"
                  style={{ height: 320, objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultIllustration;
                  }}
                />

                {images.length > 1 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {images.map((img, index) => (
                      <button
                        key={img.id}
                        type="button"
                        className="btn p-0 border-0"
                        onClick={() => setActiveImage(index)}
                      >
                        <img
                          src={normalizeBackendFileUrl(img.image)}
                          alt=""
                          className="rounded"
                          style={{
                            width: 64,
                            height: 64,
                            objectFit: "cover",
                            outline:
                              index === activeImage
                                ? "2px solid #405189"
                                : "none",
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}

                {images.length === 0 && (
                  <div className="alert alert-warning mt-2 mb-0">
                    Aucune image. Le produit ne peut pas être validé tant
                    qu&apos;une image n&apos;a pas été ajoutée.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Information */}
          <Col lg={7}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Informations</h5>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-primary"
                    onClick={() => {
                      setStockValue(String(product.stock_quantity));
                      setShowStock(true);
                    }}
                  >
                    <i className="ri-stack-line align-bottom me-1" />
                    Ajuster le stock
                  </button>
                  <Link
                    to={`/promoted-products-update/${product.id}`}
                    className="btn btn-sm btn-soft-warning"
                  >
                    <i className="ri-pencil-line align-bottom me-1" />
                    Modifier
                  </Link>
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-danger"
                    onClick={() => setShowDelete(true)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-1" />
                    Supprimer
                  </button>
                </div>
              </CardHeader>

              <CardBody>
                <h4 className="fs-18">{product.designation}</h4>
                <p className="text-muted">
                  {product.brand}
                  {product.brand && product.category?.designation ? " • " : ""}
                  {product.category?.designation}
                </p>

                <h4 className="text-primary">
                  {formatPrice(product.price, product.currency)}
                  {product.unit ? (
                    <small className="text-muted"> / {product.unit}</small>
                  ) : null}
                </h4>

                <p className="mb-3">
                  <StockStatusBadge
                    status={product.stock_status}
                    label={product.stock_status_label}
                  />
                  <span className="text-muted ms-2">
                    {product.stock_quantity} en stock
                  </span>
                </p>

                {product.description ? (
                  <>
                    <h6 className="fs-14">Description</h6>
                    <p className="text-muted">{product.description}</p>
                  </>
                ) : null}

                <Row className="mt-3">
                  <Col md={6}>
                    <h6 className="fs-14">Vendeur</h6>
                    <p className="text-muted mb-1">
                      {product.seller_name || "—"}
                    </p>
                    {product.seller_phone ? (
                      <p className="text-muted mb-1">
                        <i className="ri-phone-line me-1" />
                        {product.seller_phone}
                      </p>
                    ) : null}
                    {product.seller_email ? (
                      <p className="text-muted mb-1">
                        <i className="ri-mail-line me-1" />
                        {product.seller_email}
                      </p>
                    ) : null}
                    {product.location ? (
                      <p className="text-muted mb-0">
                        <i className="ri-map-pin-line me-1" />
                        {product.location}
                      </p>
                    ) : null}
                  </Col>

                  <Col md={6}>
                    <h6 className="fs-14">Historique</h6>
                    <p className="text-muted mb-1">
                      Créé par {product.created_by?.full_name || "—"} le{" "}
                      {dayjs(product.created_at).format("DD/MM/YYYY à HH:mm")}
                    </p>
                    <p className="text-muted mb-1">
                      Référence : {product.reference || "—"}
                    </p>
                    {product.validated_at ? (
                      <p className="text-muted mb-0">
                        Validé par {product.validated_by?.full_name || "—"} le{" "}
                        {dayjs(product.validated_at).format("DD/MM/YYYY à HH:mm")}
                      </p>
                    ) : null}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Link to="/promoted-products" className="btn btn-light">
          <i className="ri-arrow-left-line align-bottom me-1" />
          Retour à la liste
        </Link>
      </Container>
    </div>
  );
};

ProductDetails.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(ProductDetails));
